import { useCallback } from "react";

interface ControlPanelProps {
  onStart: () => void;
  onReset: () => void;
  onAutoGenerate: () => void;
  onCopy: () => void;
  isComplete: boolean;
  isStarted: boolean;
  isBallFlying: boolean;
  hasNumbers: boolean;
  currentBallIndex: number;
}

const BALL_COLORS = [
  { bg: "from-amber-400 to-amber-600", label: "1st" },
  { bg: "from-rose-400 to-rose-600", label: "2nd" },
  { bg: "from-emerald-400 to-emerald-600", label: "3rd" },
  { bg: "from-sky-400 to-sky-600", label: "4th" },
  { bg: "from-violet-400 to-violet-600", label: "5th" },
  { bg: "from-orange-400 to-orange-600", label: "6th" },
];

export default function ControlPanel({
  onStart,
  onReset,
  onAutoGenerate,
  onCopy,
  isComplete,
  isStarted,
  isBallFlying,
  hasNumbers,
  currentBallIndex,
}: ControlPanelProps) {
  const safeOnStart = useCallback(() => {
    if (typeof onStart === "function") onStart();
  }, [onStart]);

  const safeOnReset = useCallback(() => {
    if (typeof onReset === "function") onReset();
  }, [onReset]);

  const safeOnAutoGenerate = useCallback(() => {
    if (typeof onAutoGenerate === "function") onAutoGenerate();
  }, [onAutoGenerate]);

  const safeOnCopy = useCallback(() => {
    if (typeof onCopy === "function") onCopy();
  }, [onCopy]);

  return (
    <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-5 border-2 border-amber-600/40 shadow-lg shadow-amber-900/20">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-6 h-6 flex items-center justify-center text-amber-400">
          <i className="ri-gamepad-fill text-lg" />
        </div>
        <h3 className="font-bold text-amber-400 text-sm tracking-wider uppercase" style={{ fontFamily: "Orbitron, sans-serif" }}>
          Controls
        </h3>
      </div>

      <div className="space-y-3">
        {/* Status Display - only shown when game is started */}
        {isStarted && (
          <div className="w-full py-3 px-5 rounded-xl bg-gradient-to-r from-amber-900/40 to-orange-900/40 border border-amber-500/30 flex items-center justify-center gap-3">
            {isBallFlying ? (
              <div className="flex items-center gap-2">
                <i className="ri-loader-4-line text-amber-400 text-lg animate-spin" />
                <span className="text-amber-300 font-bold text-sm" style={{ fontFamily: "Orbitron, sans-serif" }}>
                  공이 굴러가는 중...
                </span>
              </div>
            ) : !isComplete ? (
              <div className="flex items-center gap-2">
                <div className={`w-4 h-4 rounded-full bg-gradient-to-br ${BALL_COLORS[currentBallIndex]?.bg || "from-amber-400 to-amber-600"}`} />
                <span className="text-amber-300 font-bold text-sm" style={{ fontFamily: "Orbitron, sans-serif" }}>
                  {currentBallIndex + 1}번 공 - 플런저를 당겨!
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <i className="ri-sparkling-2-fill text-amber-400 text-lg" />
                <span className="text-amber-300 font-bold text-sm" style={{ fontFamily: "Orbitron, sans-serif" }}>
                  완료!
                </span>
              </div>
            )}
          </div>
        )}

        {/* Ball Progress */}
        {isStarted && !isComplete && (
          <div className="flex items-center justify-center gap-2 py-2">
            {BALL_COLORS.map((col, i) => (
              <div
                key={i}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${
                  i < currentBallIndex
                    ? `bg-gradient-to-br ${col.bg} text-white shadow-md scale-100`
                    : i === currentBallIndex
                    ? `bg-gradient-to-br ${col.bg} text-white shadow-lg scale-110 ring-2 ring-white/40 animate-pulse`
                    : "bg-gray-700/50 text-gray-600 border border-dashed border-gray-600 scale-90"
                }`}
                style={{ fontFamily: "Orbitron, sans-serif" }}
              >
                {i < currentBallIndex ? "\u2713" : i + 1}
              </div>
            ))}
          </div>
        )}

        <button
          type="button"
          onClick={safeOnAutoGenerate}
          disabled={isStarted}
          className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 disabled:from-gray-700 disabled:to-gray-800 disabled:text-gray-600 text-gray-900 font-bold py-3 px-5 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer disabled:cursor-not-allowed shadow-lg shadow-amber-500/20 hover:shadow-amber-400/30 text-sm tracking-wide"
          style={{ fontFamily: "Orbitron, sans-serif" }}
        >
          <i className="ri-magic-fill text-base" />
          AUTO 6
        </button>

        <button
          type="button"
          onClick={safeOnReset}
          disabled={!hasNumbers && !isStarted}
          className="w-full bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-500 hover:to-gray-600 disabled:from-gray-700 disabled:to-gray-800 disabled:text-gray-600 text-white font-bold py-3 px-5 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer disabled:cursor-not-allowed text-sm tracking-wide"
          style={{ fontFamily: "Orbitron, sans-serif" }}
        >
          <i className="ri-restart-fill text-base" />
          RESET
        </button>

        {isComplete && (
          <button
            type="button"
            onClick={safeOnCopy}
            className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white font-bold py-3 px-5 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer shadow-lg shadow-emerald-500/20 text-sm tracking-wide animate-bounce"
            style={{ fontFamily: "Orbitron, sans-serif" }}
          >
            <i className="ri-file-copy-fill text-base" />
            번호 복사하기
          </button>
        )}
      </div>

      <div className="mt-5 p-3 bg-gray-800/60 rounded-xl border border-gray-700">
        <h4 className="font-bold text-amber-400 mb-2 text-xs tracking-wider" style={{ fontFamily: "Orbitron, sans-serif" }}>
          HOW TO PLAY
        </h4>
        <ul className="text-xs text-gray-400 space-y-1.5">
          <li className="flex items-start gap-2">
            <span className="text-red-400 mt-0.5">&#9654;</span>
            <span><strong className="text-amber-300">게임 시작</strong> 버튼을 누르세요</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-500 mt-0.5">&#9654;</span>
            <span>오른쪽 <strong className="text-amber-300">플런저</strong>를 아래로 당겨서 놓으면 발사!</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-emerald-400 mt-0.5">&#9654;</span>
            <span>세게 당길수록 공이 더 강하게 날아감</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-sky-400 mt-0.5">&#9654;</span>
            <span>공이 번호에 착지하면 다음 공 발사 가능</span>
          </li>
        </ul>
      </div>
    </div>
  );
}
